import PropTypes from "prop-types";
import React from "react";
import Vimeo from "@u-wave/react-vimeo";
import { makeStyles } from "@material-ui/core";
import { extractVideoId, VideoType } from "../Utils/misc.js";
import YtPlayer from "./YtPlayer.jsx";

const useStyles = makeStyles(() => ({
  youtubeFrame: {
    width: "100%",
    height: ({ height }) => height || 390,
    border: "none",
    marginTop: 4,
  },
  vimeoFrame: {
    width: "100%",
    height: ({ height }) => (height && height + 4) || 394,
    "&>iframe": {
      width: "100%",
      height: ({ height }) => height || 390,
      border: "none",
      marginTop: 4,
    },
  },
  customPlayer: {
    width: "100%",
    maxHeight: ({ height }) => height || 390,
  },
}));

/**
 * Воспроизводит видео разных сервисов и сообщает прогресс просмотра.
 *
 * @param {Object} props - Параметры плеера.
 * @param {string} props.url - Ссылка на видео.
 * @param {FnNumberVoid} [props.onPlayback] - Обработчик прогресса от 0 до 1.
 * @param {string|number} [props.height] - Высота плеера.
 * @returns {React.ReactElement} Видеоплеер выбранного сервиса.
 */
const UniversalVideoPlayer = ({ url, onPlayback, height }) => {
  const classes = useStyles({ height });
  const { id, videoType, startTime } = extractVideoId(url);
  const isGoogleDrive = videoType === VideoType.GOOGLE_DRIVE;
  const googleDriveResourceKey = isGoogleDrive ? new URL(url).searchParams.get("resourcekey") : null;
  const googleDriveResourceQuery = googleDriveResourceKey
    ? `resourcekey=${encodeURIComponent(googleDriveResourceKey)}`
    : "";

  /**
   * Передаёт долю просмотра видео с известной длительностью.
   *
   * @param {React.SyntheticEvent<HTMLVideoElement>} event - Событие видеоплеера.
   * @returns {void}
   */
  const videoPlaybackHandler = ({ currentTarget: player }) => {
    if (
      !onPlayback ||
      !Number.isFinite(player.duration) ||
      player.duration <= 0 ||
      !Number.isFinite(player.currentTime)
    ) {
      return;
    }
    onPlayback(Math.min(1, Math.max(0, player.currentTime / player.duration)));
  };

  /** @type {FnYtCallback} */
  const youtubePlaybackHandler = (currentTime, percent) => {
    onPlayback && onPlayback(percent);
  };

  /** @param {*} vimeo */
  const vimeoStateHandler = (vimeo) => {
    onPlayback && onPlayback(vimeo.percent);
  };

  return (
    <>
      {videoType === VideoType.YOUTUBE && (
        <YtPlayer
          className={classes.youtubeFrame}
          videoId={id}
          onPlayback={youtubePlaybackHandler}
          startTime={startTime}
        />
      )}
      {videoType === VideoType.VIMEO && (
        <Vimeo className={classes.vimeoFrame} video={url} onTimeUpdate={vimeoStateHandler} />
      )}
      {videoType === VideoType.SKILLZRUN && (
        <video
          key={url}
          className={classes.customPlayer}
          controls
          src={url}
          controlsList="nodownload"
          onTimeUpdate={videoPlaybackHandler}
          onEnded={videoPlaybackHandler}
          onContextMenu={(event) => event.preventDefault()}
        />
      )}
      {isGoogleDrive && (
        <iframe
          src={`https://drive.google.com/file/d/${id}/preview${
            googleDriveResourceQuery ? `?${googleDriveResourceQuery}` : ""
          }`}
          style={{ width: "100%", height: "394px" }}
        />
      )}
    </>
  );
};

UniversalVideoPlayer.propTypes = {
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPlayback: PropTypes.func,
  url: PropTypes.string.isRequired,
};

export default UniversalVideoPlayer;
